import {
  getLeoUuid,
  LeoUUID,
  getString,
  getOptionalString,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLRPC implements
  LeoRPC<GetPresignedDownloadURLRPC.Request, GetPresignedDownloadURLRPC.Response, GetPresignedDownloadURLRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLRPC.Response, GetPresignedDownloadURLRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    fileId: string;
    payload?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLRPCRequest: boolean = true;
  
    readonly fileId: LeoUUID;
    readonly payload: string | null;
  
  
    constructor(
    fileId: LeoUUID, 
    payload: string | null = null
    ) {
      this.fileId = fileId;
      if ( payload !== undefined && payload !== null) {
        Request.validatePayload(payload);
      }
      this.payload = payload;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const fileId = getLeoUuid(dto, "fileId", InvalidRequestFileIdError);
      const payload = getOptionalString(dto, "payload", InvalidRequestPayloadError);
      return new Request(
        fileId,
        payload
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          fileId: this.fileId.toDTO(),
      };
      if (this.payload !== undefined && this.payload !== null){
        dto.payload = this.payload;
      }
      return dto;
    }
    private static validatePayload = (payload: string): void => {
      if(payload.trim().length === 0) {
        throw new InvalidRequestPayloadNonEmptyError(
          `Attribute payload cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLRPC(fileId='${this.fileId}',payload='${this.payload}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    presignedDownloadURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLResponse: boolean = true;
  
    readonly presignedDownloadURL: URL;
  
  
    constructor(
    presignedDownloadURL: URL
    ) {
      this.presignedDownloadURL = presignedDownloadURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const presignedDownloadURL = getUrl(dto, "presignedDownloadURL", InvalidResponsePresignedDownloadUrlError);
      return new Response(
        presignedDownloadURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          presignedDownloadURL: this.presignedDownloadURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURL(presignedDownloadURL='${this.presignedDownloadURL}')`;
  };
  

  export enum RPCError {
    FileIdNotFound = "FILE_ID_NOT_FOUND"
  }

  export namespace Errors {
    
    export interface FileIdNotFoundDTO {
      code: string;
    }
    
    export class FileIdNotFound extends LeoRPCError<RPCError> {
      readonly isFileIdNotFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.FileIdNotFound;
        super(code);
      }
    
    
      toDTO(): FileIdNotFoundDTO {
        const dto: FileIdNotFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    FileIdNotFound.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLRPC.FILE_ID_NOT_FOUND()`;
    };
    

    export type Errors = FileIdNotFound;
    export type ErrorsDTO = FileIdNotFoundDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.FileIdNotFound:
          return new FileIdNotFound();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFileIdError extends GetPresignedDownloadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileIdError";
  }
}

export class InvalidRequestPayloadError extends GetPresignedDownloadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPayloadError";
  }
}

export class InvalidRequestPayloadNonEmptyError extends InvalidRequestPayloadError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPayloadNonEmptyError";
  }
}

export class InvalidResponsePresignedDownloadUrlError extends GetPresignedDownloadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePresignedDownloadUrlError";
  }
}

export class InvalidResponseFileIdNotFoundError extends GetPresignedDownloadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFileIdNotFoundError";
  }
}

